/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ロール CRUD 用の react-query フック群。権限マトリクス・ロール管理 UI で使用。
 * @graph-connects /permissions/roles [calls] ロール一覧取得・作成・更新・削除
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionsApi } from '../lib/permissions-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { CreateRoleInput, UpdateRoleInput } from '../types/permissions.js';

/** @graph-connects none */
export function useRoles() {
  return useQuery({
    queryKey: queryKeys.permissions.roles,
    queryFn: permissionsApi.listRoles,
  });
}

/** @graph-connects none */
export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => permissionsApi.createRole(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.roles });
    },
  });
}

/** @graph-connects none */
export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRoleInput }) =>
      permissionsApi.updateRole(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.roles });
      void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.me });
    },
  });
}

/** @graph-connects none */
export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => permissionsApi.deleteRole(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.roles });
      void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.me });
    },
  });
}
